import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Timestamp,
  query,
  where,
  collection,
  onSnapshot,
  Unsubscribe,
  orderBy
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Components
import CashierLayout from '../../components/layout/CashierLayout';
import TransactionHistoryList from '../../components/shared/TransactionHistoryList';
import CobrancaModal, { Cobranca } from './components/CobrancaModal';
import CobrancasAbertas from './components/CobrancasAbertas';
import TransactionPad from './components/TransactionPad';
import Alert from '../../components/ui/Alert';
import Spinner from '../../components/ui/Spinner';

// Types & API
import { ITransacao as Transacao } from '../../types/firestore.types';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '@/config/firebase.config';
import { formatarMoeda } from '@/utils/currencyUtils';
import { UNIDADE_CERVEJARIA_ID } from '@/constants/promocao';

// 🎯 CONSTANTES OTIMIZADAS
const TRANSACTION_STATUS = {
  PENDING_CONFIRMATION: 'pendente_confirmacao',
  COMPLETED: 'completed',
  VALIDATING: 'validating',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired'
} as const;

const TRANSACTION_CONFIG = {
  ALERT_DURATION: 5000,
  TIMEOUT_DURATION: 300000, // 5 minutos
  MAX_AMOUNT: 10000,
  MIN_AMOUNT: 1,
  MAX_HISTORY_ITEMS: 10
} as const;

const ANIMATION_CONFIG = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
} as const;

// 🏷️ TIPOS ESPECÍFICOS
type AlertType = 'success' | 'error' | 'warning';
type TransactionStatus = 'idle' | 'processing' | 'success' | 'error';

interface AlertState {
  tipo: AlertType;
  mensagem: string;
}

interface EstatisticasDia {
  // Totais gerais
  totalHoje: number;
  totalTransacoes: number;

  // Detalhamento por status
  transacoesCompletas: number;
  transacoesPendentes: number;
  transacoesValidando: number;
  transacoesComErro: number;

  // Valores por status
  valorCompletas: number;
  valorPendentes: number;
  valorValidando: number;
  valorComErro: number;

  // Métricas avançadas
  ticketMedio: number;
  maiorTransacao: number;
  menorTransacao: number;

  // Timeline
  primeiraTransacao: Date | null;
  ultimaTransacao: Date | null;

  // Performance
  transacoesPorHora: { [hora: string]: number };
  valorPorHora: { [hora: string]: number };
}

const unidades = [{ unidade_id: '1', label: 'Mr. Tugas Aeroporto' }, { unidade_id: '2', label: 'Mr. Tugas Cervejaria' }, { unidade_id: '3', label: 'Mr. Tugas Jardim Norte' }];

// ✅ CORREÇÃO: Função para obter data da transação (COMPATÍVEL COM useSaldo)
const getDataTransacao = (transacao: Transacao): Date | null => {
  if (transacao.criado_em) {
    return transacao.criado_em instanceof Timestamp
      ? transacao.criado_em.toDate()
      : new Date(transacao.criado_em);
  } else if (transacao.createdAt) {
    return transacao.createdAt instanceof Timestamp
      ? transacao.createdAt.toDate()
      : new Date(transacao.createdAt);
  } else if (transacao.data_hora) {
    return transacao.data_hora instanceof Timestamp
      ? transacao.data_hora.toDate()
      : new Date(transacao.data_hora);
  }
  return null;
};

// ✅ CORREÇÃO: Função para verificar se uma transação é de hoje
const isToday = (transacao: Transacao): boolean => {
  const dataTransacao = getDataTransacao(transacao);
  if (!dataTransacao) return false;

  const today = new Date();

  return (
    dataTransacao.getDate() === today.getDate() &&
    dataTransacao.getMonth() === today.getMonth() &&
    dataTransacao.getFullYear() === today.getFullYear()
  );
};

const useTransactionListener = (unidadeId: string | undefined) => {
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!unidadeId) {
      setLoading(false);
      return;
    }

    const allQuery = query(collection(db, 'transacoes'));

    const unsubscribe = onSnapshot(allQuery, (snapshot) => {

      // 🎯 CORREÇÃO: Converter para string na comparação
      const unidadeIdString = String(unidadeId);

      const transacoesFiltradas = snapshot.docs
        .filter(doc => {
          const data = doc.data();
          // Converter ambos para string para comparar
          return String(data.unidade_id) === unidadeIdString;
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Transacao));

      setTransacoes(transacoesFiltradas);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [unidadeId]);

  return { transacoes, loading, error: null };
};

const useAlertSystem = () => {
  const [alerta, setAlerta] = useState<AlertState | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const exibirAlerta = useCallback((tipo: AlertType, mensagem: string) => {
    setAlerta({ tipo, mensagem });

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Auto-dismiss alert
    timeoutRef.current = setTimeout(() => {
      setAlerta(null);
    }, TRANSACTION_CONFIG.ALERT_DURATION);
  }, []);

  const limparAlerta = useCallback(() => {
    setAlerta(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { alerta, exibirAlerta, limparAlerta };
};

// 🧮 UTILITIES - CORRIGIDAS
const calcularEstatisticasDetalhadas = (transacoes: Transacao[]): EstatisticasDia => {
  // ✅ CORREÇÃO: Usar função isToday atualizada
  const transacoesHoje = transacoes.filter(transacao => isToday(transacao));

  if (transacoesHoje.length === 0) {
    return {
      totalHoje: 0,
      totalTransacoes: 0,
      transacoesCompletas: 0,
      transacoesPendentes: 0,
      transacoesValidando: 0,
      transacoesComErro: 0,
      valorCompletas: 0,
      valorPendentes: 0,
      valorValidando: 0,
      valorComErro: 0,
      ticketMedio: 0,
      maiorTransacao: 0,
      menorTransacao: 0,
      primeiraTransacao: null,
      ultimaTransacao: null,
      transacoesPorHora: {},
      valorPorHora: {}
    };
  }

  // ✅ CORREÇÃO: Agrupa por status atualizado
  const transacoesCompletas = transacoesHoje.filter(t => t.status === TRANSACTION_STATUS.COMPLETED);
  const transacoesPendentes = transacoesHoje.filter(t => t.status === TRANSACTION_STATUS.PENDING_CONFIRMATION);
  const transacoesValidando = transacoesHoje.filter(t => t.status === TRANSACTION_STATUS.VALIDATING);
  const transacoesComErro = transacoesHoje.filter(t =>
    t.status === TRANSACTION_STATUS.FAILED ||
    t.status === TRANSACTION_STATUS.CANCELLED ||
    t.status === TRANSACTION_STATUS.EXPIRED
  );

  // Calcula valores por status
  const valorCompletas = transacoesCompletas.reduce((sum, t) => sum + (t.valor || 0), 0);
  const valorPendentes = transacoesPendentes.reduce((sum, t) => sum + (t.valor || 0), 0);
  const valorValidando = transacoesValidando.reduce((sum, t) => sum + (t.valor || 0), 0);
  const valorComErro = transacoesComErro.reduce((sum, t) => sum + (t.valor || 0), 0);

  // Encontra maiores e menores valores
  const valores = transacoesHoje.map(t => t.valor || 0).filter(valor => valor > 0);
  const maiorTransacao = valores.length > 0 ? Math.max(...valores) : 0;
  const menorTransacao = valores.length > 0 ? Math.min(...valores) : 0;

  // ✅ CORREÇÃO: Encontra timestamps usando getDataTransacao
  const timestamps = transacoesHoje
    .map(transacao => getDataTransacao(transacao))
    .filter((date): date is Date => date !== null)
    .sort((a, b) => a.getTime() - b.getTime());

  // ✅ CORREÇÃO: Agrupa por hora usando getDataTransacao
  const transacoesPorHora: { [hora: string]: number } = {};
  const valorPorHora: { [hora: string]: number } = {};

  transacoesHoje.forEach(transacao => {
    const data = getDataTransacao(transacao);
    if (!data) return;

    const hora = `${data.getHours().toString().padStart(2, '0')}:00`;

    transacoesPorHora[hora] = (transacoesPorHora[hora] || 0) + 1;
    valorPorHora[hora] = (valorPorHora[hora] || 0) + (transacao.valor || 0);
  });

  const totalValor = valorCompletas + valorPendentes + valorValidando + valorComErro;

  return {
    totalHoje: totalValor,
    totalTransacoes: transacoesHoje.length,

    transacoesCompletas: transacoesCompletas.length,
    transacoesPendentes: transacoesPendentes.length,
    transacoesValidando: transacoesValidando.length,
    transacoesComErro: transacoesComErro.length,

    valorCompletas,
    valorPendentes,
    valorValidando,
    valorComErro,

    ticketMedio: transacoesHoje.length > 0 ? totalValor / transacoesHoje.length : 0,
    maiorTransacao,
    menorTransacao,

    primeiraTransacao: timestamps.length > 0 ? timestamps[0] : null,
    ultimaTransacao: timestamps.length > 0 ? timestamps[timestamps.length - 1] : null,

    transacoesPorHora,
    valorPorHora
  };
};

const validarTransacao = (valor: number, usuarioValido: boolean) => {
  if (!usuarioValido) {
    return { valido: false, erro: 'Operador não autenticado' };
  }

  if (valor < TRANSACTION_CONFIG.MIN_AMOUNT) {
    return {
      valido: false,
      erro: `Valor mínimo: R$ ${TRANSACTION_CONFIG.MIN_AMOUNT.toFixed(2)}`
    };
  }

  if (valor > TRANSACTION_CONFIG.MAX_AMOUNT) {
    return {
      valido: false,
      erro: `Valor máximo: R$ ${TRANSACTION_CONFIG.MAX_AMOUNT.toFixed(2)}`
    };
  }

  return { valido: true, erro: null };
};

// 🎨 COMPONENTES DE UI
const TransactionStatusIndicator: React.FC<{ status: TransactionStatus }> =
  React.memo(({ status }) => {
    if (status === 'idle') return null;
    const statusConfig = {
      processing: {
        text: '🔄 Processando pagamento...',
        color: 'bg-yellow-500 border-yellow-600'
      },
      success: {
        text: '✅ Pagamento confirmado!',
        color: 'bg-green-500 border-green-600'
      },
      error: {
        text: '❌ Erro no pagamento',
        color: 'bg-red-500 border-red-600'
      }
    };

    const config = statusConfig[status];

    return (
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className={`fixed top-4 right-4 ${config.color} text-white px-4 py-3 rounded-lg 
                 shadow-lg z-50 border backdrop-blur-sm`}
      >
        <span className="font-medium">{config.text}</span>
      </motion.div>
    );
  });

const DetailedStatsCard: React.FC<{ estatisticas: EstatisticasDia; }> = React.memo(({ estatisticas }) => {
  const [showDetails, setShowDetails] = useState(false);
  const StatusItem: React.FC<{
    icon: string;
    label: string;
    quantidade: number;
    valor: number;
    colorClass: string;
  }> = ({ icon, label, quantidade, valor, colorClass }) => (
    <div className={`p-4 rounded-lg border-l-4 ${colorClass} bg-opacity-10 ${colorClass.replace('border', 'bg')}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{icon}</span>
          <span className="font-medium text-gray-700">{label}</span>
        </div>
        <div className="text-right">
          <div className="font-bold text-gray-900">{quantidade}</div>
          <div className="text-sm text-primary">R$ {valor.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl shadow-lg border border-primary/20 overflow-hidden mx-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-white to-primary p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-primary font-semibold text-lg">Resumo Detalhado do Dia</h4>
            <p className="text-black text-sm">
              {new Date().toLocaleDateString('pt-BR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className=" text-white px-3 py-1 bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] rounded-lg transition-colors text-sm font-medium"
          >
            {showDetails ? '↥ Resumir' : '↧ Detalhes'}
          </button>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="p-4">
        {/* Métricas Principais */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] rounded-lg">
            <div className="text-2xl font-bold text-white">
              R$ {estatisticas.totalHoje.toFixed(2)}
            </div>
            <div className="text-sm text-white font-medium">Valor Total</div>
          </div>
          <div className="text-center p-3 bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663]  rounded-lg">
            <div className="text-2xl font-bold text-white">
              {estatisticas.totalTransacoes}
            </div>
            <div className="text-sm text-white font-medium">Total Transações</div>
          </div>
        </div>

        {/* Ticket Médio */}
        <div className="text-center p-3 bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663]  rounded-lg mb-4">
          <div className="text-lg font-bold text-white">
            R$ {estatisticas.ticketMedio.toFixed(2)}
          </div>
          <div className="text-sm text-white">Ticket Médio</div>
        </div>

        {/* Detalhes Expandíveis */}
        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 overflow-hidden"
            >
              {/* Status das Transações */}
              <div className="space-y-2  p-4 bg-white rounded-lg">
                <h5 className="font-semibold text-white">Status das Transações</h5>
                <StatusItem
                  icon="✅"
                  label="Completas"
                  quantidade={estatisticas.transacoesCompletas}
                  valor={estatisticas.valorCompletas}
                  colorClass="border-green-500"
                />
                <StatusItem
                  icon="⏳"
                  label="Pendentes Confirmação"
                  quantidade={estatisticas.transacoesPendentes}
                  valor={estatisticas.valorPendentes}
                  colorClass="border-orange-500"
                />
                <StatusItem
                  icon="🔍"
                  label="Em Validação"
                  quantidade={estatisticas.transacoesValidando}
                  valor={estatisticas.valorValidando}
                  colorClass="border-blue-500"
                />
                <StatusItem
                  icon="❌"
                  label="Com Erro"
                  quantidade={estatisticas.transacoesComErro}
                  valor={estatisticas.valorComErro}
                  colorClass="border-red-500"
                />
              </div>

              {/* Extremos */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] rounded-lg">
                  <div className="text-sm text-white font-medium">Maior Transação</div>
                  <div className="font-bold text-white">
                    R$ {estatisticas.maiorTransacao.toFixed(2)}
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] rounded-lg">
                  <div className="text-sm text-white font-medium">Menor Transação</div>
                  <div className="font-bold text-white">
                    R$ {estatisticas.menorTransacao.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="p-3 rounded-lg">
                <h5 className="font-semibold text-white mb-2">Horário das Transações</h5>
                <div className="text-sm text-primary space-y-1">
                  <div>⏰ Primeira: {estatisticas.primeiraTransacao
                    ? estatisticas.primeiraTransacao.toLocaleTimeString('pt-BR')
                    : 'N/A'}</div>
                  <div>🕒 Última: {estatisticas.ultimaTransacao
                    ? estatisticas.ultimaTransacao.toLocaleTimeString('pt-BR')
                    : 'N/A'}</div>
                </div>
              </div>

              {/* Gráfico Simples por Hora */}
              {Object.keys(estatisticas.transacoesPorHora).length > 0 && (
                <div className="p-3 bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] rounded-lg">
                  <h5 className="font-semibold text-white mb-2">Movimento por Hora</h5>
                  <div className="space-y-2">
                    {Object.entries(estatisticas.transacoesPorHora)
                      .sort(([horaA], [horaB]) => horaA.localeCompare(horaB))
                      .map(([hora, quantidade]) => (
                        <div key={hora} className="flex items-center justify-between text-sm">
                          <span className="text-primary">{hora}</span>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{quantidade} trans.</span>
                            <span className="text-white font-medium">
                              R$ {estatisticas.valorPorHora[hora].toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
});

// 🎯 COMPONENTE PRINCIPAL
const CashierDashboardPage: React.FC = () => {
  const { usuario } = useAuth();

  // Estados
  // Cobrança aberta no modal (QR + código). Fechar o modal não cancela a cobrança:
  // ela continua em "Cobranças em aberto" até ser paga ou cancelada.
  const [cobrancaModalId, setCobrancaModalId] = useState<string | null>(null);
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatus>('idle');

  // Hooks personalizados
  const { alerta, exibirAlerta, limparAlerta } = useAlertSystem();
  const { transacoes, loading: carregandoTransacoes, error: erroTransacoes } =
    useTransactionListener(usuario?.unidade_id);

  // 👤 Validação de usuário
  const usuarioValido = useMemo(() =>
    Boolean(usuario?.id && usuario?.unidade_id),
    [usuario]
  );

  const ehCervejaria = String(usuario?.unidade_id) === UNIDADE_CERVEJARIA_ID;

  // 📊 Estatísticas detalhadas em tempo real
  const estatisticasDetalhadas = useMemo(() =>
    calcularEstatisticasDetalhadas(transacoes),
    [transacoes]
  );

  // 🧾 Cria a cobrança no servidor e abre o QR + código. O pagamento é
  // conferido e debitado no servidor quando o cliente paga: não há mais
  // confirmação pelo navegador do caixa, nem débito pendente.
  // O TransactionPad não desabilita o botão: a trava evita duas cobranças num clique duplo.
  const criandoCobrancaRef = useRef(false);
  const handleTransactionRequest = useCallback(async (valor: number, numeroComanda: string) => {
    if (criandoCobrancaRef.current) return false;

    const validacao = validarTransacao(valor, usuarioValido);
    if (!validacao.valido) {
      exibirAlerta('error', validacao.erro!);
      return false;
    }

    criandoCobrancaRef.current = true;
    setTransactionStatus('processing');
    limparAlerta();

    try {
      const resultado = await httpsCallable(getFunctions(), 'criarCobranca')({
        valor: Number(valor.toFixed(2)),
        numeroComanda: numeroComanda.trim(),
      });
      const { success, error, cobrancaId } = resultado.data as { success: boolean; error?: string; cobrancaId?: string };
      if (!success || !cobrancaId) {
        exibirAlerta('error', error || 'Não foi possível criar a cobrança.');
        return false;
      }
      setCobrancaModalId(cobrancaId);
      return true;
    } catch (error) {
      console.error('❌ Erro ao criar cobrança:', error);
      exibirAlerta('error', 'Não foi possível criar a cobrança. Tente novamente.');
      return false;
    } finally {
      criandoCobrancaRef.current = false;
      setTransactionStatus('idle');
    }
  }, [usuarioValido, exibirAlerta, limparAlerta]);

  // Uma cobrança saiu de "em aberto": avisa o caixa quando foi paga.
  const handleCobrancaEncerrada = useCallback((cobranca: Cobranca) => {
    if (cobranca.status === 'paga') {
      exibirAlerta('success', `✅ Cobrança ${cobranca.codigo} paga: ${formatarMoeda(cobranca.valor)}`);
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    }
  }, [exibirAlerta]);

  // 🎨 RENDER
  return (
    <CashierLayout>
      <div className="min-h-screen">
        {/* Estatísticas Detalhadas */}
        <DetailedStatsCard estatisticas={estatisticasDetalhadas} />
        <AnimatePresence>
          {transactionStatus !== 'idle' && (
            <TransactionStatusIndicator status={transactionStatus} />
          )}
        </AnimatePresence>

        <motion.div
          initial={ANIMATION_CONFIG.initial}
          animate={ANIMATION_CONFIG.animate}
          transition={{ duration: 0.5 }}
          className="container mx-auto p-4"
        >
          {/* Sistema de Alertas */}
          <AnimatePresence>
            {alerta && (
              <motion.div
                initial={ANIMATION_CONFIG.initial}
                animate={ANIMATION_CONFIG.animate}
                exit={ANIMATION_CONFIG.exit}
                className="mb-4"
              >
                <Alert
                  tipo={alerta.tipo}
                  mensagem={alerta.mensagem}
                  onClose={limparAlerta}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Layout Principal */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Painel Principal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Header */}
              <div className="rounded-xl border border-primary/20 p-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-white">Painel do Caixa</h1>
                    <p className="text-primary mt-1">
                      Bem-vindo, <span className="font-semibold text-white">
                        {usuario?.nome || 'Operador'}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Unidade</div>
                    <div className="text-white font-medium">{unidades.find((i) => i.unidade_id == usuario?.unidade_id)?.label}</div>
                  </div>
                </div>
                <p className="mt-4 pt-3 border-t border-primary/20 text-sm text-primary">
                  Tugas Coins: consumo no local, retirada e eventos na Cave. Não aceitar no delivery de chope nem no delivery de pizza.
                </p>
                {ehCervejaria && (
                  <p className="mt-2 text-sm font-semibold text-red-400">
                    Nesta unidade não é aceito o saldo da Promoção de Aniversário 21 anos. O sistema recusa sozinho e mostra o motivo.
                  </p>
                )}
              </div>

              {/* Painel de Transações */}
              <div className="rounded-xl  border border-primary/20 p-6 shadow-lg">
                <TransactionPad
                  onTransactionRequest={handleTransactionRequest}
                  onClear={!!cobrancaModalId}
                  disabled={transactionStatus === 'processing'}
                />
              </div>

              {usuario?.unidade_id && (
                <CobrancasAbertas
                  unidadeId={String(usuario.unidade_id)}
                  onVer={setCobrancaModalId}
                  onEncerrada={handleCobrancaEncerrada}
                />
              )}
            </motion.div>

            {/* Sidebar - Histórico */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <div className="rounded-xl  border border-primary/20 p-6 shadow-lg h-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Transações Recentes</h3>
                  <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    {transacoes.filter(t => isToday(t)).length} hoje
                  </span>
                </div>

                {carregandoTransacoes ? (
                  <div className="flex justify-center items-center h-32">
                    <Spinner size="lg" />
                    <span className="text-gray-500 ml-3">Carregando transações...</span>
                  </div>
                ) : erroTransacoes ? (
                  <Alert tipo="error" mensagem={erroTransacoes} />
                ) : transacoes.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl">📊</div>
                    <p className="text-gray-500 mt-2">Nenhuma transação encontrada</p>
                  </div>
                ) : (
                  <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                    <TransactionHistoryList
                      transacoes={transacoes
                        .filter(t => isToday(t))
                        .slice(0, TRANSACTION_CONFIG.MAX_HISTORY_ITEMS)}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Modal da cobrança (QR + código) */}
        <CobrancaModal
          cobrancaId={cobrancaModalId}
          onClose={() => setCobrancaModalId(null)}
        />
      </div>
    </CashierLayout>
  );
};

export default React.memo(CashierDashboardPage);